import {
  QUERY_TRANSITIONS_V3,
  type QueryTransitionsV3Query,
  type QueryTransitionsV3QueryVariables,
} from '@revelio/data-access';

import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getTransitionsV3Variables } from '../../filters/utils/get-transitions-v3-variables';
import {
  effectiveFiltersWithSinglePrimaryEntity,
  useChartPrimaryFilter,
} from '../../hooks/useChartPrimaryFilter';
import { useChart } from '../Chart/ChartProvider';
import type { TransitionSecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type { TransitionMetricValues } from '../PageConfigProvider/types/metric';
import type { BreakdownSingleDataSet, DataProviderChildren } from './types';
import { convertTransitionsV3Data } from './utils/convert-transitions-v3-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type TransitionDataOutput = BreakdownSingleDataSet;

export const TransitionV3DataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension?: TransitionSecondaryDimensionValues;
  metric: TransitionMetricValues;
  children: DataProviderChildren<TransitionDataOutput>;
}) => {
  const { item } = useChart();
  const { usesStateBreakdownOverMsa } = useFilterContext();

  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });

  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });
  const filters = effectiveFiltersWithSinglePrimaryEntity({
    item,
    effectiveFilters,
    chartPrimaryFilter,
  });

  const variables = getTransitionsV3Variables({
    filters,
    primaryDimension,
    usesStateBreakdownOverMsa,
  });

  if (!variables) return null;

  return (
    <QueryTransitionsV3 variables={variables}>
      {({ data, status }) => {
        const transitionData = convertTransitionsV3Data({
          data: data?.transitions_v3?.entities ?? [],
          secondaryDimension,
          metric,
        });

        return children({
          data: transitionData,
          status,
        });
      }}
    </QueryTransitionsV3>
  );
};

type QueryTransitionsV3Props = {
  variables: QueryTransitionsV3QueryVariables;
  children: DataProviderChildren<QueryTransitionsV3Query>;
};
const QueryTransitionsV3 = ({
  variables,
  children,
}: QueryTransitionsV3Props) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus({
    query: QUERY_TRANSITIONS_V3,
    variables,
    pause: !isTaxonomyResolved,
  });

  return children({ data, status });
};
